import React, { useCallback, useMemo } from 'react';
import DiamondEmpty from '@uber/base-icons/diamond-empty';
import Document from '@uber/base-icons/document';
import { toaster } from 'baseui/toast';
import { ANNOTATION_LABELS } from '../../../common/constant/constant';
import { ANNOTATION_LABEL } from '../../../common/constant/error_constant';
import useStorage from '../../../common/hooks/useStorage';
import type { SelectLabelPayloadT } from '../../../common/side-panel/analytics/types';
import useAnalyticsForSidePanel from '../../../common/side-panel/analytics/useAnalyticsForSidePanel';
import type { AnnotationLabelT } from '../../../common/types/types';
import { hex2rgba } from '../../../common/utils/hex2rgba';
import { rgbStringToArray } from '../../../common/utils/rgb-string-to-array';
import { rgbToHex } from '../../../common/utils/utils';
import { SIDE_PANEL_ACTIONS, TASK_QUESTIONNAIRE_ID, TASK_QUESTIONNAIRE_TITLE } from '../const';
import { useSidePanel } from '../side-panel-provider';
import type { TabsType } from '../types';
import { useAnnotationLabel } from './use-annotation-labels';
import { useAnnotations } from './use-annotations';
import { useDispatch } from './use-dispatch';
import { useInitializeLabelColors } from './use-initialize-label-colors';
import { useSelectedAnnotationLabel } from './use-selected-annotation-label';
const convertColorToRgba = (color: string, opacity = 1): string => {
  if (color.startsWith('rgb')) {
    return color;
  }
  if (color.startsWith('#')) {
    try {
      const [r, g, b] = hex2rgba(color, opacity);
      return `rgb(${r}, ${g}, ${b})`;
    } catch {
      return color;
    }
  }
  return color;
};
type UseLabelListDataParams = {
  annotationsTabKey: TabsType;
  filterText?: string;
  onOpenTaskQuestionnaire?: () => void;
};
export const useLabelListData = ({
  annotationsTabKey,
  filterText = '',
  onOpenTaskQuestionnaire
}: UseLabelListDataParams) => {
  const {
    toolConfig,
    setActiveTab,
    labelListItemEnhancer,
    taskQuestionnaireComponent
  } = useSidePanel();
  const {
    sendEventForLabelSelection
  } = useAnalyticsForSidePanel();
  const annotationLabels = useAnnotationLabel();
  const selectedAnnotationLabel = useSelectedAnnotationLabel();
  const annotations = useAnnotations();
  const dispatch = useDispatch();
  const {
    set
  } = useStorage();
  useInitializeLabelColors();
  const annotationCountMap = useMemo(() => {
    return annotations.reduce<Record<string, number>>((map, annotation) => {
      const labelTitle = annotation.annotationLabel.title;
      map[labelTitle] = (map[labelTitle] ?? 0) + 1;
      return map;
    }, {});
  }, [annotations]);
  const getRenderIcon = useCallback((label: AnnotationLabelT) => {
    function IconRenderer({
      strokeColor
    }: {
      strokeColor: string;
    }) {
      const labelTool = label.tools?.length === 1 ? label.tools[0] : undefined;
      const DisplayIcon = labelTool && toolConfig[labelTool]?.Icon;
      if (DisplayIcon) {
        return React.createElement(DisplayIcon, {
          strokeColor
        });
      }
      return React.createElement(DiamondEmpty, {
        color: strokeColor
      });
    }
    return IconRenderer;
  }, [toolConfig]);
  const normalizedFilter = filterText.trim().toLowerCase();
  const shouldShowTaskQuestionnaire = !!onOpenTaskQuestionnaire && !!taskQuestionnaireComponent && (!normalizedFilter || TASK_QUESTIONNAIRE_TITLE.toLowerCase().includes(normalizedFilter));
  const filterOptions = useMemo(() => {
    const labelOptions = annotationLabels.map(label => ({
      label: label.title,
      value: label.title,
      strokeColor: label.style.strokeColor,
      renderIcon: getRenderIcon(label)
    }));
    if (!onOpenTaskQuestionnaire || !taskQuestionnaireComponent) {
      return labelOptions;
    }
    return [...labelOptions, {
      label: TASK_QUESTIONNAIRE_TITLE,
      value: TASK_QUESTIONNAIRE_ID,
      strokeColor: 'transparent',
      renderIcon: () => React.createElement(Document)
    }];
  }, [annotationLabels, getRenderIcon, onOpenTaskQuestionnaire, taskQuestionnaireComponent]);
  const visibleAnnotationLabels = useMemo(() => {
    if (!normalizedFilter) {
      return annotationLabels;
    }
    return annotationLabels.filter(label => label.title.toLowerCase().includes(normalizedFilter));
  }, [annotationLabels, normalizedFilter]);
  const onLabelClick = useCallback((name: string) => {
    if (name === TASK_QUESTIONNAIRE_ID) {
      onOpenTaskQuestionnaire?.();
      return;
    }
    const annotationLabel = annotationLabels.find(label => label.title === name);
    if (!annotationLabel) {
      return;
    }
    const isSelected = annotationLabel.title === selectedAnnotationLabel?.title;
    if (isSelected) {
      dispatch({
        type: SIDE_PANEL_ACTIONS.SELECT_ANNOTATION_LABEL,
        annotationLabel: null
      });
      return;
    }
    dispatch({
      type: SIDE_PANEL_ACTIONS.SELECT_ANNOTATION_LABEL,
      annotationLabel
    });
    const eventPayload: SelectLabelPayloadT = {
      label: annotationLabel.title
    };
    sendEventForLabelSelection(eventPayload);
  }, [annotationLabels, dispatch, onOpenTaskQuestionnaire, selectedAnnotationLabel?.title, sendEventForLabelSelection]);
  const handleOnCountClick = useCallback((name: string) => {
    setActiveTab(annotationsTabKey);
    dispatch({
      type: SIDE_PANEL_ACTIONS.SELECT_FILTER,
      labelTitle: name
    });
  }, [annotationsTabKey, dispatch, setActiveTab]);
  const handleLabelColorChange = useCallback((colorPickerPayload: {
    color: string;
    title: string;
  }) => {
    const {
      color,
      title
    } = colorPickerPayload;
    const isDuplicateColor = annotationLabels.some(annotationLabel => {
      const existingColor = annotationLabel.style.fillColor;
      let hexColor = existingColor;
      if (existingColor.startsWith('rgb(')) {
        const rgbColor = rgbStringToArray(existingColor);
        hexColor = rgbToHex(rgbColor[0], rgbColor[1], rgbColor[2]);
      }
      return hexColor.toLowerCase() === color.toLowerCase() && title.toLowerCase() !== annotationLabel.title.toLowerCase();
    });
    if (isDuplicateColor) {
      toaster.negative(ANNOTATION_LABEL.DUPLICATE_COLOR_ERROR, {
        autoHideDuration: 5000
      });
      return false;
    }
    const rgbColor = convertColorToRgba(color);
    const updatedTitleStyleMap = annotationLabels.reduce((updatedMap, annotationLabel) => {
      const {
        strokeColor,
        fillColor
      } = annotationLabel.style;
      const labelTitle = annotationLabel.title;
      const style = {
        ...annotationLabel.style,
        strokeColor: labelTitle === colorPickerPayload.title ? rgbColor : strokeColor,
        fillColor: labelTitle === colorPickerPayload.title ? rgbColor : fillColor
      };
      return {
        ...updatedMap,
        [labelTitle]: style
      };
    }, ({} as Record<string, AnnotationLabelT['style']>));
    dispatch({
      type: 'UPDATE_ANNOTATION_LABELS',
      color: rgbColor,
      title: title
    });
    set(ANNOTATION_LABELS, JSON.stringify(updatedTitleStyleMap) || '{}');
    return true;
  }, [annotationLabels, dispatch, set]);
  return {
    annotationLabels,
    filterOptions,
    visibleAnnotationLabels,
    selectedAnnotationLabel,
    annotationCountMap,
    onLabelClick,
    handleOnCountClick,
    handleLabelColorChange,
    labelListItemEnhancer,
    getRenderIcon,
    taskQuestionnaireComponent,
    shouldShowTaskQuestionnaire
  };
};